import { Modal } from "flowbite-react";
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import ButtonWithLoader from "../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../common/components/form/InputFormik";
import TextareaFormik from "../../renters/components/forms/TextareaFormik";
import { useToastContext } from "../../../common/components/ui/toast/toast.context";
import ComboBoxFormik from "../../../common/components/form/ComboBoxFormik";
import { ASSET_CATEGORY_LABELS, DEPARTMENT_LABELS, IinventoryManagement, TAX_REGIME_LABELS } from "../../../types/inventory-management.type";
import { ASSET_STATUS_LABELS } from "./TableInventoryManagement";
import { FileInputPropsInventoryManagement } from "./FileInputPropsInventoryManagement";
import { inventoryManagementDeleteFile, inventoryManagementSendFile, updateInventoryManagement } from "../../../common/services/Inventory-management.api";
import { useEmployeers } from "../hooks";
import { useState } from "react";

export type Props = {
    inventoryManagement?: IinventoryManagement;
    onClose: () => void;
    onSave: (inventoryManagement: IinventoryManagement) => void;
    onChange: (inventoryManagement: IinventoryManagement) => void;
};


const inventoryManagementSchema = Yup.object().shape({
    assetCode: Yup.string().required('Código patrimonial do bem é obrigatório'),
    description: Yup.string().required('Descrição é obrigatória'),
    category: Yup.string().required('Categoria é obrigatória'),
    departmentEnum: Yup.string().required('Departamento é obrigatório'),
    currentStatus: Yup.string().required('Status do Imobilizado é obrigatório'),
    taxRegime: Yup.string().required('Regime Tributário é obrigatório'),
    acquisitionDate: Yup.date().required('Data de aquisição é obrigatória'),
    acquisitionValue: Yup.number()
        .typeError('Valor de Aquisição deve ser um número')
        .required('Valor de Aquisição é obrigatório'),
});


export function ModalEditInventoryManagement(props: Props) {
    const [, setIsLoading] = useState(false);
    const { notifyError, notifySuccess } = useToastContext();
    const pathToUpload = `inventory-management/${props.inventoryManagement?._id || ''}`;

    const { data: employeers } = useEmployeers()

    const saveInventoryManagementNF = async (nf) => {
        try {
            if (!nf)
                return notifyError("Nota Fiscal não pode ser vazia");

            if (!props.inventoryManagement)
                return notifyError("Item não encontrado");

            const updated = { ...props.inventoryManagement, invoiceDocumentPath: nf };

            const result = await inventoryManagementSendFile(updated);

            if (!result.ok)
                return notifyError(result.message || "Erro ao salvar a Nota Fiscal");

            props.onChange(result.data);

            return notifySuccess("Nota Fiscal salva com sucesso");
        } catch (error) {
            console.error("ERRO CAPTURADO:", error);
        }
    }

    const deleteFile = async (): Promise<boolean> => {
        try {
            if (!props.inventoryManagement)
                return false;

            const updated = { ...props.inventoryManagement, invoiceDocumentPath: "" };

            const result = await inventoryManagementDeleteFile(updated);

            if (!result.ok) {
                notifyError(result.message || "Erro ao excluir o arquivo");
                return false;
            }

            notifySuccess("Arquivo excluído com sucesso");
            props.onChange(updated);
            return true;
        } catch (error) {
            return false;
        }
    }

    const handleSubmit = async (values) => {
        setIsLoading(true);
        try {

            const result = await updateInventoryManagement(values);

            if (!result.ok)
                return notifyError(result.message || "Erro ao criar o item de imobilizado");

            props.onSave(result.data);

            props.onClose();
        } catch (error) {
            console.error("Erro ao salvar o agendamento:", error);
            return notifyError("Erro ao criar o item de imobilizado");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            className="relative text-black"
            show={true}
            onClose={() => props.onClose()}
            size="7x"
            theme={{
                content: {
                    base: "relative h-full w-full p-4 md:h-auto",
                    inner: "relative rounded-xl bg-white shadow-lg max-w-[90%] max-h-[80vh] mx-auto overflow-hidden flex flex-col"
                }
            }}
        >
            <Modal.Header className="text-black border-b border-gray-200">
                <h2 className="text-2xl font-bold text-black">Editar item de imobilizado</h2>
            </Modal.Header>
            <Modal.Body className="min-h-[509px] bg-white">
                <Formik
                    initialValues={{
                        ...props.inventoryManagement,
                        acquisitionDate: props.inventoryManagement?.acquisitionDate
                            ? (props.inventoryManagement.acquisitionDate instanceof Date
                                ? props.inventoryManagement.acquisitionDate.toISOString().slice(0, 10)
                                : new Date(props.inventoryManagement.acquisitionDate).toISOString().slice(0, 10))
                            : "",
                    }}
                    validationSchema={inventoryManagementSchema}
                    onSubmit={handleSubmit as unknown as typeof handleSubmit}
                >
                    {({ isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <div className="space-y-8 p-4">

                                {/* Identificação */}
                                <section>
                                    <h3 className="text-lg font-semibold text-black mb-4">Identificação</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
                                        <InputFormik
                                            label="Código patrimonial do bem:"
                                            name="assetCode"
                                            className="w-full"
                                            required={true}
                                        />
                                        <InputFormik
                                            label="Número de seŕie:"
                                            name="serieNumber"
                                            className="w-full"
                                        />
                                        <ComboBoxFormik
                                            label="Categoria:"
                                            name="category"
                                            className="w-full"
                                            required={true}
                                            options={[
                                                { value: '', label: 'Selecione uma categoria...' },
                                                ...Object.entries(ASSET_CATEGORY_LABELS).map(([value, label]) => ({
                                                    value,
                                                    label,
                                                }))
                                            ]}
                                        />
                                        <ComboBoxFormik
                                            label="Departamento:"
                                            name="departmentEnum"
                                            className="w-full"
                                            required={true}
                                            options={[
                                                { value: '', label: 'Selecione um departamento...' },
                                                ...Object.entries(DEPARTMENT_LABELS).map(([value, label]) => ({
                                                    value,
                                                    label,
                                                }))
                                            ]}
                                        />
                                        <ComboBoxFormik
                                            label="Status do Imobilizado:"
                                            name="currentStatus"
                                            className="w-full"
                                            required={true}
                                            options={[
                                                { value: '', label: 'Selecione o status...' },
                                                ...Object.entries(ASSET_STATUS_LABELS).map(([value, label]) => ({
                                                    value,
                                                    label,
                                                }))
                                            ]}
                                        />
                                        <div className="flex-1 min-w-[220px]">
                                            <ComboBoxFormik
                                                label="Colaborador:"
                                                name="employeerId"
                                                className="w-full"
                                                required={false}
                                                options={[{ value: '', label: 'Selecione o colaborador...' }, 
                                                  ...(employeers?.map((employee) => ({
                                                  value: employee._id ?? "",
                                                  label: employee.name ?? "",
                                                })) || [])]}
                                            />
                                        </div>
                                    </div>
                                    <InputFormik
                                        label="Descrição:"
                                        name="description"
                                        className="w-full"
                                        required={true}
                                        type="textarea"
                                        placeholder="Descreva o bem..."
                                    />
                                </section>

                                <section>
                                    <h3 className="text-lg font-semibold text-black mb-4">Informações Fiscais e Tributárias</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-2">
                                        <ComboBoxFormik
                                            label="Regime Tributário:"
                                            name="taxRegime"
                                            required={true}
                                            className="w-full"
                                            options={[
                                                { value: '', label: 'Selecione um regime...' },
                                                ...Object.entries(TAX_REGIME_LABELS).map(([value, label]) => ({
                                                    value,
                                                    label,
                                                }))
                                            ]}
                                        />
                                        <InputFormik
                                            label="Alíquota PIS/COFINS (%):"
                                            name="pisCofinsRate"
                                            type="number"
                                            className="w-full"
                                            placeholder="0,00"
                                        />
                                        <div className="flex justify-center gap-2">
                                            <div className="flex items-center gap-2 w-full">
                                                <input
                                                    id="fiscalIncentive"
                                                    name="fiscalIncentive"
                                                    type="checkbox"
                                                    checked={!!values.fiscalIncentive}
                                                    onChange={e => setFieldValue("fiscalIncentive", e.target.checked)}
                                                    className="h-5 w-5 accent-green-600"
                                                />
                                                <label htmlFor="fiscalIncentive" className="font-medium">Incentivo Fiscal?</label>
                                            </div>
                                            <div className="flex items-center gap-2 w-full">
                                                <input
                                                    id="icmsCreditable"
                                                    name="icmsCreditable"
                                                    type="checkbox"
                                                    checked={!!values.icmsCreditable}
                                                    onChange={e => setFieldValue("icmsCreditable", e.target.checked)}
                                                    className="h-5 w-5 accent-green-600"
                                                />
                                                <label htmlFor="icmsCreditable" className="font-medium">Gera Crédito de ICMS?</label>
                                            </div>
                                        </div>
                                    </div>

                                    <h3 className="text-lg font-semibold text-black mb-4 mt-6">Informações Contábeis</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-2">
                                        <InputFormik
                                            label="Data de aquisição:"
                                            name="acquisitionDate"
                                            type="date"
                                            className="w-full"
                                            required={true}
                                        />
                                        <InputFormik
                                            label="Valor de Aquisição (R$):"
                                            name="acquisitionValue"
                                            type="number"
                                            className="w-full"
                                            required={true}
                                            placeholder="0,00"
                                        />
                                        <InputFormik
                                            label="Valor Residual (R$):"
                                            name="residualValue"
                                            type="number"
                                            className="w-full"
                                            placeholder="0,00"
                                        />
                                        <InputFormik
                                            label="Depreciação Anual (%):"
                                            name="depreciationRate"
                                            type="number"
                                            className="w-full"
                                            placeholder="0,00"
                                        />
                                        <InputFormik
                                            label="Vida Útil (meses):"
                                            name="usefulLifeInMonths"
                                            type="number"
                                            className="w-full"
                                            placeholder="0"
                                        />
                                        <InputFormik
                                            label="Valor Líquido Atual (R$)"
                                            name="netValue"
                                            type="number"
                                            className="w-full"
                                            placeholder="0,00"
                                        />
                                    </div>
                                </section>

                                {/* Nota Fiscal */}
                                <section>
                                    <h3 className="text-lg font-semibold text-black mb-4">Nota Fiscal</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        <InputFormik
                                            label="Chave da Nota Fiscal:"
                                            name="invoiceKey"
                                            type="text"
                                            className="w-full"
                                        />
                                        <FileInputPropsInventoryManagement
                                            onChange={async (nf) => {
                                                await saveInventoryManagementNF(nf);
                                                setFieldValue("invoiceDocumentPath", nf);
                                            }}
                                            onDelete={async () => {
                                                const result = await deleteFile();
                                                if (!result) return notifyError("Erro ao excluir o arquivo");
                                                setFieldValue("invoiceDocumentPath", "");
                                            }}
                                            pathToUpload={pathToUpload}
                                            currentPath={values.invoiceDocumentPath || ''}
                                            nameFile="Enviar Nota Fiscal"
                                        />
                                    </div>
                                </section>
                                <section>
                                    <label htmlFor="notes" className="block mb-2 text-sm font-medium text-gray-700">
                                        Observações:
                                    </label>
                                    <TextareaFormik
                                        label=""
                                        name="notes"
                                        className="w-full min-h-[120px] rounded shadow-sm border border-gray-200 p-2"
                                        placeholder="Descreva informações relevantes..."
                                    />
                                </section>
                                <div className="flex justify-end pt-6">
                                    <ButtonWithLoader
                                        isLoading={isSubmitting}
                                        disabled={isSubmitting}
                                        className="bg-[#e44131] text-white px-6 py-2 rounded-lg font-semibold shadow hover:bg-[#c12f1a] transition"
                                    >
                                        Salvar
                                    </ButtonWithLoader>
                                </div>
                            </div>
                        </Form>
                    )}
                </Formik>
            </Modal.Body>
        </Modal>
    );
}
